/**
 * .js-zoomArea内の要素をpanzoomで拡縮・パン可能にする
 * - マウスホイール・ピンチで拡縮
 * - ドラッグ・スワイプで移動
 * - .js-zoomArea-zoomIn / .js-zoomArea-zoomOut のクリックで拡縮
 * - 初期表示はコンテナ内に収まるようにフィット
 * - SP時のみstageページ: PDF.jsによる再描画で拡縮（画質優先）
 */

import Panzoom from '@panzoom/panzoom'

const SP_BREAKPOINT = 768
const isStagePage = () => document.getElementById('page-stage')
const isSP = () => window.matchMedia(`(max-width: ${SP_BREAKPOINT}px)`).matches
const usePdfZoom = () => isStagePage() && isSP()

/**
 * 利用可能な高さを取得（iOS等の100vh問題・visualViewportを考慮）
 * getBoundingClientRectの実際の描画サイズとvisualViewportの小さい方を採用
 */
function getAvailableHeight(container) {
  if (typeof window === 'undefined' || !container) return container?.clientHeight ?? 0
  const rect = container.getBoundingClientRect()
  const vv = window.visualViewport
  const innerH = window.innerHeight
  let h = Math.floor(rect.height)
  if (vv && vv.height > 0) {
    h = Math.min(h, Math.floor(vv.height * 0.92))
  }
  if (innerH > 0) {
    h = Math.min(h, Math.floor(innerH * 0.88))
  }
  return Math.max(0, h)
}

/**
 * コンテナに収まるスケールを計算（はみ出さない）
 * @param {HTMLElement} container - ビューポート（親要素）
 * @param {HTMLElement} content - コンテンツ（view要素）
 * @returns {number} フィットスケール（1を超えない）
 */
function getFitScale(container, content) {
  const rect = container.getBoundingClientRect()
  const cw = Math.floor(rect.width)
  const ch = getAvailableHeight(container)
  const cwContent = content.scrollWidth
  const chContent = content.scrollHeight
  if (cw <= 0 || ch <= 0 || cwContent <= 0 || chContent <= 0) return 1
  const isTouch = 'ontouchstart' in window || navigator.maxTouchPoints > 0
  const SAFETY = isTouch ? 0.85 : 0.9
  return Math.min((cw * SAFETY) / cwContent, (ch * SAFETY) / chContent, 1)
}

/**
 * スケール時にコンテナ中央に収まるパン値を計算
 * transform-origin 50% 50% のとき、スケール後の左上は (Vw/2*(1-S), Vh/2*(1-S))
 * 中央配置にするため pan = (希望の左上) - (現在の左上)
 */
function getCenterPan(container, content, scale) {
  const cw = container.clientWidth
  const ch = container.clientHeight
  const cwContent = content.scrollWidth
  const chContent = content.scrollHeight
  const displayW = cwContent * scale
  const displayH = chContent * scale
  const desiredLeft = (cw - displayW) / 2
  const desiredTop = (ch - displayH) / 2
  const currentLeft = (cwContent / 2) * (1 - scale)
  const currentTop = (chContent / 2) * (1 - scale)
  return {
    x: desiredLeft - currentLeft,
    y: desiredTop - currentTop,
  }
}

function initPdfZoom(zoomAreas) {
  const STEP = 0.25
  const MIN = 0.5
  const MAX = 3

  zoomAreas.forEach((zoomArea) => {
    const view = zoomArea.querySelector('.js-zoomArea-view')
    const zoomInBtn = zoomArea.querySelector('.js-zoomArea-zoomIn')
    const zoomOutBtn = zoomArea.querySelector('.js-zoomArea-zoomOut')
    const pdfContainer = zoomArea.querySelector('.js-stTable-pdf')
    const dialog = zoomArea.closest('dialog')

    if (!view || !pdfContainer) return

    view.classList.add('is-pdfZoom')
    pdfContainer.classList.add('is-pdfZoom')
    let zoomLevel = 1

    function render(fit) {
      if (typeof window.stagePdfRenderAtScale !== 'function') return
      window.stagePdfRenderAtScale(pdfContainer, zoomLevel, fit)
    }

    if (dialog) {
      dialog.addEventListener('close', () => {
        zoomLevel = 1
      })
      const scheduleRender = () => {
        if (!dialog.open) return
        zoomLevel = 1
        const attempt = (n = 0) => {
          const viewEl = zoomArea.querySelector('.js-zoomArea-view')
          const h = viewEl ? viewEl.clientHeight : 0
          if (h > 50 || n >= 5) {
            render(true)
            return
          }
          setTimeout(() => attempt(n + 1), 80)
        }
        setTimeout(() => attempt(0), 50)
      }
      const mo = new MutationObserver(scheduleRender)
      mo.observe(dialog, { attributes: true, attributeFilter: ['open'] })
      zoomArea._stagePdfScheduleRender = scheduleRender
    }

    if (zoomInBtn) {
      zoomInBtn.addEventListener('click', (e) => {
        e.preventDefault()
        if (zoomLevel >= MAX) return
        zoomLevel = Math.min(zoomLevel + STEP, MAX)
        render()
      })
    }
    if (zoomOutBtn) {
      zoomOutBtn.addEventListener('click', (e) => {
        e.preventDefault()
        if (zoomLevel <= MIN) return
        zoomLevel = Math.max(zoomLevel - STEP, MIN)
        render()
      })
    }
  })
}

function initZoomAreas() {
  const zoomAreas = document.querySelectorAll('.js-zoomArea')
  if (!zoomAreas.length) return

  if (usePdfZoom()) {
    initPdfZoom(zoomAreas)
    const orig = window.modalOpen
    if (typeof orig === 'function') {
      window.modalOpen = function (targetName) {
        orig(targetName)
        if (/^map-\d+$/.test(targetName)) {
          const modal = document.querySelector('.js-modal--' + targetName)
          const za = modal?.querySelector('.js-zoomArea')
          if (za?._stagePdfScheduleRender) za._stagePdfScheduleRender()
        }
      }
    }
    return
  }

  zoomAreas.forEach((zoomArea) => {
    const view = zoomArea.querySelector('.js-zoomArea-view')
    const zoomInBtn = zoomArea.querySelector('.js-zoomArea-zoomIn')
    const zoomOutBtn = zoomArea.querySelector('.js-zoomArea-zoomOut')

    if (!view) return

    const container = view.parentElement || zoomArea

    const panzoom = Panzoom(view, {
      maxScale: 5,
      minScale: 0.125,
      step: 0.3,
      startScale: 1,
      startX: 0,
      startY: 0,
    })

    let initialFitDone = false
    function applyFitScale() {
      if (initialFitDone) return
      const fitScale = getFitScale(container, view)
      if (fitScale > 0 && fitScale < 1) {
        panzoom.zoom(fitScale, { animate: false })
        panzoom.setOptions({ minScale: fitScale })
        requestAnimationFrame(() => {
          const { x, y } = getCenterPan(container, view, fitScale)
          panzoom.pan(x, y, { animate: false })
        })
        initialFitDone = true
      }
    }

    requestAnimationFrame(() => {
      requestAnimationFrame(applyFitScale)
    })

    const resizeObserver = new ResizeObserver(applyFitScale)
    resizeObserver.observe(view)

    if (container) {
      container.addEventListener('wheel', (e) => panzoom.zoomWithWheel(e), { passive: false })
    }

    if (zoomInBtn) {
      zoomInBtn.addEventListener('click', (e) => {
        e.preventDefault()
        panzoom.zoomIn()
      })
    }
    if (zoomOutBtn) {
      zoomOutBtn.addEventListener('click', (e) => {
        e.preventDefault()
        panzoom.zoomOut()
      })
    }
  })
}

// DOM準備後に初期化
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', initZoomAreas)
} else {
  initZoomAreas()
}
